/**
 * HUB-G — Fetch parallèle + merge timeline overview.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  computeTimelineKpis,
  isInDateRange,
  mergeActivityEvents,
} from '../../utils/activite/activityEvents';
import { matchesPaymentSearch } from '../../utils/paiements/paiementsDisplay';
import { matchesRepairSearch } from '../../utils/reparations/reparationsDisplay';
import { matchesMovementSearch } from '../../utils/stock/stockDisplay';
import { matchesCaisseSearch } from '../../utils/caisse/caisseDisplay';

export default function useActiviteTimeline(api, filters, { enabled = true, isAdmin = false } = {}) {
  const [ventes, setVentes] = useState([]);
  const [sumupPayments, setSumupPayments] = useState([]);
  const [stripePayments, setStripePayments] = useState([]);
  const [caisseSessions, setCaisseSessions] = useState([]);
  const [stockMovements, setStockMovements] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');

  const fetchAll = useCallback(async () => {
    if (!api || !enabled) return;
    setLoading(true);
    setError(null);

    const histParams = new URLSearchParams({ page: '1', limit: '25' });
    if (filters.vendeur) histParams.append('vendeur', filters.vendeur);
    if (filters.date_debut) histParams.append('date_debut', filters.date_debut);
    if (filters.date_fin) histParams.append('date_fin', filters.date_fin);
    if (filters.search) histParams.append('search', filters.search);
    if (filters.magasin_id) histParams.append('magasin_id', filters.magasin_id);

    const movParams = new URLSearchParams({ page: '1', limit: '20' });
    if (filters.magasin_id) movParams.append('magasin_id', filters.magasin_id);
    if (filters.date_debut && filters.date_fin) {
      movParams.append('date_debut', filters.date_debut);
      movParams.append('date_fin', filters.date_fin);
    }

    const deviceParams = new URLSearchParams({ limit: '25', include_delivered: 'true' });
    if (filters.magasin_id) deviceParams.append('magasin_id', filters.magasin_id);
    if (filters.search) deviceParams.append('search', filters.search);

    const caisseParams = new URLSearchParams();
    if (filters.date_debut) caisseParams.append('date_debut', filters.date_debut);
    if (filters.date_fin) caisseParams.append('date_fin', filters.date_fin);
    if (filters.magasin_id) caisseParams.append('magasin_id', filters.magasin_id);

    try {
      const requests = [
        api.get(`/ventes/historique?${histParams.toString()}`),
        api.get('/payments/sumup/all'),
        api.get('/payments/stripe/sales'),
        api.get(`/stock-movements?${movParams.toString()}`),
        api.get(`/devices?${deviceParams.toString()}`),
      ];
      if (isAdmin) {
        requests.push(api.get(`/caisse/historique?${caisseParams.toString()}`));
      }

      const results = await Promise.allSettled(requests);
      const get = (idx, fallback) => (results[idx]?.status === 'fulfilled' ? results[idx].value.data : fallback);

      setVentes(get(0, {})?.ventes || []);
      setSumupPayments(get(1, []) || []);
      setStripePayments(get(2, []) || []);
      setStockMovements(get(3, {})?.movements || []);
      setRepairs(get(4, []) || []);
      setCaisseSessions(isAdmin ? (get(5, []) || []) : []);

      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length === results.length) {
        setError('Impossible de charger la timeline');
      } else if (failed.length > 0) {
        setError(null);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Erreur chargement timeline');
      setVentes([]);
      setSumupPayments([]);
      setStripePayments([]);
      setCaisseSessions([]);
      setStockMovements([]);
      setRepairs([]);
    } finally {
      setLoading(false);
    }
  }, [api, enabled, filters, isAdmin]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const filteredSumup = useMemo(
    () => sumupPayments.filter(
      (p) => isInDateRange(p.updated_at || p.created_at, filters.date_debut, filters.date_fin)
        && matchesPaymentSearch(p, filters.search, { kind: 'sumup' }),
    ),
    [sumupPayments, filters],
  );

  const filteredStripe = useMemo(
    () => stripePayments.filter(
      (p) => isInDateRange(p.updated_at || p.created_at, filters.date_debut, filters.date_fin)
        && matchesPaymentSearch(p, filters.search, { kind: 'stripe' }),
    ),
    [stripePayments, filters],
  );

  const filteredMovements = useMemo(
    () => stockMovements.filter((m) => matchesMovementSearch(m, filters.search)),
    [stockMovements, filters.search],
  );

  const filteredRepairs = useMemo(
    () => repairs.filter(
      (d) => isInDateRange(d.updated_at || d.created_at, filters.date_debut, filters.date_fin)
        && matchesRepairSearch(d, filters.search),
    ),
    [repairs, filters],
  );

  const filteredCaisse = useMemo(
    () => caisseSessions.filter((s) => matchesCaisseSearch(s, filters.search)).slice(0, 12),
    [caisseSessions, filters.search],
  );

  const events = useMemo(
    () => mergeActivityEvents({
      ventes,
      sumupPayments: filteredSumup,
      stripePayments: filteredStripe,
      caisseSessions: filteredCaisse,
      stockMovements: filteredMovements,
      repairs: filteredRepairs,
      filters,
      typeFilter,
    }),
    [
      ventes,
      filteredSumup,
      filteredStripe,
      filteredCaisse,
      filteredMovements,
      filteredRepairs,
      filters,
      typeFilter,
    ],
  );

  const kpis = useMemo(() => computeTimelineKpis(events), [events]);

  return {
    events,
    kpis,
    loading,
    error,
    typeFilter,
    setTypeFilter,
    refresh: fetchAll,
  };
}
